import { useQuery } from '@tanstack/react-query';
import { Response } from 'datx-jsonapi';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';

import {
  getSessionHref,
  getSessionsListHref,
  getSessionTree,
  SessionTree,
} from '@/components/event/SessionsList/utils';
import { DropdownSelect } from '@/components/ui/shared/DropdownSelect/DropdownSelect';
import { SearchCategories } from '@/enums';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { queryThinEventSessionsByQueryInfinite } from '@/queries/thin-event-sessions';
import { eventConfigurationQueryOptions } from '@/queries/upgrade/eventConfiguration';
import { ThinEventSession } from '@/resources/ThinEventSession';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringParam } from '@/utils/url-query-string';

import { IDropdownItem } from '../EventDropdown';

function renderItem(session: ThinEventSession | null, eventId: string, tree: SessionTree, label?: string) {
  const href = session ? getSessionHref(session, eventId, tree) : getSessionsListHref(eventId, tree);

  return {
    id: session?.id ?? '',
    value: label ?? session.name,
    href,
    as: href,
  };
}

function prepareSessions(
  eventSessions: Array<ThinEventSession>,
  eventId: string,
  tree: SessionTree,
  defaultValueLabel: string
) {
  const defaultValue = renderItem(null, eventId, tree, defaultValueLabel || 'all sessions');
  const values = eventSessions?.map((session: ThinEventSession) => renderItem(session, eventId, tree));

  return [defaultValue, ...values];
}

interface IEventLectureDropdownProps {
  scope: 'lectures' | 'posters';
  maxW?: string | Record<string, string>;
}

export const EventSessionsDropdown: FC<IEventLectureDropdownProps> = ({ scope, maxW = { md: '250px' } }) => {
  const router = useRouter();
  const { eventId, sessionId, trackIds } = useQueryParams<{
    eventId: string;
    sessionId: string;
    trackIds: string;
  }>({
    eventId: StringParam,
    sessionId: StringParam,
    trackId: StringParam,
  });

  const tree = getSessionTree(router.pathname);

  const { data: eventConfiguration } = useQuery(eventConfigurationQueryOptions(eventId));

  const filterScope = scope === 'lectures' ? 'with_lectures' : 'with_posters';

  const { data: sessions, isValidating, error, size, setSize } = useResourcesInfinite(
    queryThinEventSessionsByQueryInfinite({
      filters: {
        event_id: eventId,
        scope: filterScope,
        listed: 'true',
        track_id: trackIds,
        poster_only: scope === SearchCategories.POSTERS ? 'true' : 'false',
      },
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { items, isReachingEnd, isLoadingMore: isLoadingSessions } = useMemo(
    () => extractSWRInfiniteData<ThinEventSession>(sessions as Array<Response<ThinEventSession>>, error, size),
    [sessions, error, size]
  );

  const onClickItem = (item: IDropdownItem) => {
    router.push(item.href);
  };

  const options = prepareSessions(items, eventId, tree, eventConfiguration?.sessions_label_name);
  const selectedOption = options.find((o) => o.id === sessionId);

  return (
    <DropdownSelect
      matchWidth={true}
      maxW={maxW}
      options={options}
      selectedOption={selectedOption?.value ? selectedOption : options[0]}
      onClickOption={onClickItem}
      setSize={setSize}
      size={size}
      isLoading={isLoadingSessions}
      error={error}
      isReachingEnd={isReachingEnd}
    />
  );
};
